/**
 * โหมดงานของเครื่องสแกนหน้างาน (Two-Scan Model — D-027/D-028)
 *
 * แยกออกจาก component เพื่อให้ react-refresh ทำงานถูกต้อง และเพื่อให้ทั้งเมนู
 * (`ScannerMenu.tsx`) และตาราง route ของโมดูล (`App.tsx`) เรนเดอร์จากลิสต์นี้
 * ที่เดียว — เพิ่มโมดูลที่สี่คือการเพิ่มรายการหนึ่งที่นี่ ไม่ใช่การไปแก้หน้าจอ
 * (D-109 ข้อ 4)
 *
 * แต่ละโหมดมีหน้าจอของตัวเองที่ **เก็บค่าที่ป้อนแล้วเข้าคิวออฟไลน์หนึ่งใบ**
 * (D-075) — ไม่ post ตรงไปเซิร์ฟเวอร์ การส่งขึ้นไปเป็นหน้าที่ของ sync engine
 * ซึ่งยังไม่มีในบิลด์นี้
 *
 * เดิมโหมดเป็น **state ในหน้าจอ** ไม่ใช่ route เพราะ "การผูกกับ URL ไม่ได้ให้
 * อะไรกับเครื่องที่ไม่มีแถบที่อยู่" — เหตุผลนั้น**จริง**ตอนที่เมนู ฟอร์มของโมดูล
 * ที่เลือก และแผงสถานะออฟไลน์ยัดอยู่ในหน้าเดียวกันหมด แต่ D-109 แยกเครื่องสแกน
 * เป็นสองมุมมอง (เมนู ↔ โมดูลเต็มจอ) เงื่อนไขที่เหตุผลเดิมยืนอยู่จึงเปลี่ยนไปแล้ว:
 * พอมีสองมุมมอง ปุ่ม Back ของเครื่อง Android **ต้อง**พากลับเมนู ไม่ใช่หลุดออก
 * จากแอปกลางงาน ซึ่งทำได้ด้วย history entry ของ route จริงเท่านั้น ไม่ใช่ state
 * ในหน้าเดียว — `path` ด้านล่างจึงเพิ่มเข้ามาให้แต่ละโหมดเป็น route segment ย่อย
 * ของ `SCANNER_PATH` ควบคู่กับการเป็นตัวระบุโหมดเดิม (D-109 ข้อ 2, ดู `routes.ts`)
 */

export type ScannerMode = 'TAG_SCAN' | 'STORE_STAGING' | 'SCAN_IN' | 'SCAN_OUT';

export interface ScannerModeOption {
  readonly id: ScannerMode;
  readonly label: string;
  readonly description: string;
  readonly step: string;
  /** emoji แทนไอคอนของโมดูลนี้ — ใช้กับทุกหน้าที่อ้างถึงโมดูลนี้ (D-107 ข้อ 5) */
  readonly icon: string;
  /** route segment ย่อยของ `SCANNER_PATH` — ประกอบเป็น URL เต็มผ่าน `scannerModulePath` ใน `routes.ts` */
  readonly path: string;
}

export const SCANNER_MODES: readonly ScannerModeOption[] = [
  {
    id: 'TAG_SCAN',
    label: 'สแกนป้าย Kanban',
    description: 'สแกนป้าย OUTPUT_TAG เพื่อดูสถานะแพ็คเกจและสิ่งที่ควรทำต่อ',
    step: 'ตรวจป้าย',
    icon: '🏷️',
    path: 'tag-scan',
  },
  {
    id: 'STORE_STAGING',
    label: 'จัดเตรียมวัตถุดิบ (Store Staging)',
    description: 'สแกน lot ต้นทางเพื่อย้ายเข้าจุด STAGING ข้างไลน์และจองให้ใบสั่งผลิต',
    step: 'ขั้นที่ 1',
    icon: '📦',
    path: 'store-staging',
  },
  {
    id: 'SCAN_IN',
    label: 'สแกนเข้า (Scan-In)',
    description: 'เปิดรอบงานที่ work center — ยังไม่ตัดสต๊อก',
    step: 'ขั้นที่ 2',
    icon: '📥',
    path: 'scan-in',
  },
  {
    id: 'SCAN_OUT',
    label: 'สแกนออก (Scan-Out)',
    description: 'ปิดรอบงาน บันทึกจำนวนดี/เสีย/แก้ไข แล้วให้เซิร์ฟเวอร์ backflush',
    step: 'ขั้นที่ 3',
    icon: '📤',
    path: 'scan-out',
  },
];
